export const billingConfiguration = {
  header: {
    title: "Billing configuration",
    description:
      "Manage your account's billing issuers and link each listing to the right one.",
  },

  intro: {
    lead:
      "A <strong>billing issuer</strong> is the tax entity that invoices your guests. You can have several and choose which one invoices each listing.",
    feats: {
      fiscalData: {
        label: "Own tax details",
        tip: "Legal name, NIF/CIF and tax address shown on the invoice.",
      },
      series: {
        label: "Independent series",
        tip: "Each issuer keeps its own series and sequence (and for corrective invoices), never mixed.",
      },
      verifactu: {
        label: "VeriFactu per issuer",
        tip: "Each issuer meets VeriFactu (signing and AEAT registration) separately, per its tax setup.",
      },
      assignment: {
        label: "Assignment per listing",
        tip: "Link each listing to an issuer. Unassigned ones use the primary issuer.",
      },
    },
    foot: "When issuing, the issuer of the booking's listing is used (at check-out or when the guest requests it from the AI agent).",
  },

  issuers: {
    card: {
      title: "Billing issuers",
      subtitle: "Create multiple issuers and assign your listings to each one.",
    },
    searchPlaceholder: "Search issuer by name or NIF…",
    newButton: "New issuer",
    noName: "No name",
    defaultBadge: "Primary",
    listingsCount: "{count} listings",
    empty: {
      noMatch: "No issuer matches your search.",
      none: "You don't have any issuers yet. Create the first one with “New issuer”.",
    },
  },

  assign: {
    card: {
      title: "Listing assignment",
      subtitle:
        "Choose which issuer invoices each listing. A listing can have only one issuer; unassigned ones use the primary.",
    },
    searchPlaceholder: "Search listing…",
    filter: {
      all: "All",
      unassigned: "Unassigned",
    },
    issuerFallback: "Issuer #{id}",
    principalSuffix: " (primary)",
    unassignedOption: "— Unassigned (primary)",
    empty: {
      noMatch: "No listing matches the filter.",
      none: "You don't have any listings yet.",
    },
    count: "{shown} of {total} listings",
  },

  drawer: {
    editTitle: "Edit issuer",
    newTitle: "New issuer",
    subtitle: "Tax details of the billing issuer",
    closeAria: "Close",
    footer: {
      isDefault: "It's the primary",
      setDefault: "Set as primary",
      delete: "Delete",
      cancel: "Cancel",
      saving: "Saving...",
      save: "Save issuer",
    },
  },

  company: {
    title: "Company details",
    subtitle: "Tax information for your property",
    country: {
      label: "Billing country",
      hint: "Choose the tax country that applies to this configuration.",
    },
    personType: {
      label: "Entity type",
      company: "Legal entity (company)",
      individual: "Individual (sole trader)",
    },
    legalName: {
      label: "Name / Legal name",
      placeholderCompany: "e.g. INNDEV S.L.",
      placeholderIndividual: "e.g. María García",
      hint: "Tax name of the company or individual",
    },
    co: {
      regime: {
        label: "DIAN regime",
        common: "Common regime",
        simplified: "Simplified regime",
        hint: "Required for DIAN electronic invoicing",
      },
      municipality: {
        label: "DIAN municipality",
        hint: "DIVIPOLA code of your municipality — required by DIAN.",
        selected: "Selected: {code}",
      },
      responsibilities: {
        label: "Tax responsibilities",
        placeholder: "e.g. O-13, R-99-PN",
        hint: "Comma-separated codes. Most common: <strong>O-13</strong>",
      },
    },
    mx: {
      regime: {
        label: "Tax regime (SAT)",
        placeholder: "e.g. 601",
        hint: "SAT tax regime code",
      },
      zip: {
        label: "Tax ZIP (SAT)",
        placeholder: "e.g. 06600",
        hint: "SAT tax postal code",
      },
    },
  },

  address: {
    title: "Tax address",
    subtitle: "The address shown on invoices",
    street: {
      label: "Street and number",
    },
    city: {
      label: "City",
      hint: "City and postal code",
    },
    email: {
      label: "Billing email",
      placeholder: "billing@yourproperty.com",
    },
    phone: {
      label: "Phone",
    },
    ph: {
      ES: {
        street: "e.g. 42 Gran Vía St., floor 3",
        city: "Madrid",
        zip: "28001",
        regionLabel: "Province",
        region: "Madrid",
        phone: "+34 123 456 789",
      },
      CO: {
        street: "e.g. Calle 13 No. 10-25",
        city: "Bogotá",
        zip: "110111",
        regionLabel: "Department",
        region: "Cundinamarca",
        phone: "+57 601 234 5678",
      },
      MX: {
        street: "e.g. Av. Insurgentes Sur 1234",
        city: "Mexico City",
        zip: "06600",
        regionLabel: "State",
        region: "CDMX",
        phone: "+52 55 1234 5678",
      },
    },
  },

  bank: {
    title: "Account for payments",
    subtitle: "Where you get paid when the payment method is bank transfer",
    iban: {
      label: "Account number (IBAN)",
      placeholder: "ES91 2100 0418 4502 0005 1332",
      hint: "Printed on the invoice only when the payment method is bank transfer.",
      error: "The account number (IBAN) is not valid.",
    },
    bic: {
      label: "BIC / SWIFT",
      placeholder: "CAIXESBB",
      hint: "Optional. Needed for payments from outside the SEPA area.",
    },
    holder: {
      label: "Account holder",
      placeholder: "Leave blank if it matches the legal name",
      hint: "Only if the account is in a different name from the company.",
    },
  },

  invoice: {
    title: "Invoice configuration",
    subtitle: "Format, currency and numbering of your documents",
    currency: {
      label: "Default currency",
      hint: "Currency in which invoices will be issued",
    },
    taxRate: {
      label: "Default IVA %",
      hintCO: "General IVA Colombia: <strong>19%</strong>",
      hintMX: "General IVA Mexico: <strong>16%</strong>",
      hintES: "Reduced hospitality IVA Spain: <strong>10%</strong> — general: <strong>21%</strong>",
    },
    type: {
      label: "Invoice type",
      detailed: {
        label: "Detailed",
        desc: "Each service on its own line",
      },
      simplified: {
        label: "Simplified",
        desc: "Receipt or invoice with grouped items",
      },
    },
    numbering: {
      label: "Document numbering",
      example: "e.g. {value}",
      invoices: {
        title: "Invoices",
      },
      series: "Series",
      optional: "(optional)",
      optionalPlaceholder: "Optional",
      digits: "Digits",
      nextNumber: "Next number",
      hintOptional: "The series is optional. If left empty, the invoice will be issued with the sequential number only.",
      hintFormat: "If you set a series, the system will build the code as SERIES-NUMBER. Without a series, it will produce NUMBER only.",
      hintWarn: "Set this value before issuing the first invoice of the series to keep the fiscal sequence aligned.",
      hintSandboxCO: "In sandbox use <strong>SETP</strong> — in production it must match your DIAN resolution.",
    },
    creditNotes: {
      title: "Corrective invoices",
      badge: "Voids / corrections",
      hint: "Used for the next corrective invoice or void you issue.",
    },
    footer: {
      label: "Default footer",
      hint: "Text that will appear at the bottom of all invoices",
      phES: "e.g. Transaction subject to reduced hospitality IVA under art. 91 of the IVA Law",
      phCO: "e.g. DIAN Resolution No. 000123 of 01-01-2025. Ranges 1 to 5000000",
      phMX: "e.g. CFDI issued per the requirements of article 29-A of the CFF",
    },
  },

  advanced: {
    title: "Advanced options",
    subtitle: "Additional delivery settings",
    attachPdf: {
      label: "Attach PDF",
      text: "Include the PDF document alongside the download link",
    },
  },

  loading: {
    default: "Loading...",
    config: "Loading configuration...",
    profiles: "Loading issuers...",
    saving: "Saving configuration...",
  },

  countries: {
    ES: "Spain",
    CO: "Colombia",
    MX: "Mexico",
  },

  currencies: {
    EUR: "Euro",
    COP: "Colombian peso",
    USD: "US dollar",
    MXN: "Mexican peso",
  },

  taxId: {
    labelFallback: "Tax ID",
    helpES: "NIF for individuals, CIF for companies. e.g. B12345678",
    helpCO: "NIT with check digit. e.g. 9014514812 (10 digits)",
    helpMX: "RFC of 12 or 13 characters. e.g. XAXX010101000",
    helpFallback: "Tax identifier",
  },

  validation: {
    taxIdES: "Invalid format. e.g. B12345678",
    taxIdCO: "The NIT must have 9 or 10 digits. e.g. 9014514812",
    taxIdMX: "Invalid RFC format. e.g. XAXX010101000",
  },

  msg: {
    reassignError: "Couldn't reassign the listing.",
    defaultUpdated: "Primary issuer updated.",
    setDefaultError: "Couldn't set as primary.",
    deleteConfirm: "Delete this billing issuer?",
    deleted: "Issuer deleted.",
    deleteError: "Couldn't delete the issuer.",
    requiredFields: "Complete all required fields before saving.",
    fixTaxId: "Fix the tax identifier before saving.",
    coRegimeRequired: "The DIAN regime is required for Colombia.",
    coMunicipalityRequired: "The DIAN municipality is required for Colombia.",
    savedOk: "Issuer saved successfully.",
    saveError: "Error saving the configuration.",
  },

  misc: {
    thisIssuer: "this issuer",
  },
};
